import { useEffect } from "react";
import { keys } from "@/lib/i18n/portal/index";
import { useTranslations } from "@/lib/i18n/intl";

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let meta = document.head.querySelector<HTMLMetaElement>(selector);
  if (!meta) {
    meta = document.createElement("meta");
    document.head.appendChild(meta);
  }

  const previousValues = new Map<string, string | null>();
  for (const [key, value] of Object.entries(attributes)) {
    previousValues.set(key, meta.getAttribute(key));
    meta.setAttribute(key, value);
  }

  return () => {
    let shouldRemove = true;
    for (const [key, previousValue] of previousValues.entries()) {
      if (previousValue === null) {
        meta.removeAttribute(key);
      } else {
        meta.setAttribute(key, previousValue);
        shouldRemove = false;
      }
    }

    if (shouldRemove) {
      meta.remove();
    }
  };
}

export function PwaHead() {
  const t = useTranslations();

  useEffect(() => {
    // The manifest link + apple-touch-icon are static in index.html. Here we
    // only manage the iOS-specific meta tags (Safari still requires the
    // apple-* prefix; the standard `mobile-web-app-capable` is in index.html).
    const cleanups = [
      upsertMeta('meta[name="apple-mobile-web-app-capable"]', {
        name: "apple-mobile-web-app-capable",
        content: "yes",
      }),
      upsertMeta('meta[name="apple-mobile-web-app-status-bar-style"]', {
        name: "apple-mobile-web-app-status-bar-style",
        content: "default",
      }),
      upsertMeta('meta[name="apple-mobile-web-app-title"]', {
        name: "apple-mobile-web-app-title",
        content: t(keys.auth.sellerTitle),
      }),
    ];

    return () => {
      for (const cleanup of cleanups.reverse()) {
        cleanup();
      }
    };
  }, [t]);

  return null;
}
